import { filterXSS } from 'xss';

import { addDays, nextDay, safeParseClientDate, startOfDay } from 'shared/util/datetime-fns';
import { queryParamToString } from 'shared/util/query';
import { stringToDayOfWeek } from '../dayOfWeek';
import type { CheckboxMap, LngLat, MapBounds, OpenNumberRange, RoomWithPeople, SortOption } from '../../interfaces';

const today = () => startOfDay(Date.now()).getTime();

export const removeEmptyValues = <T>(obj: T): T => {
    Object.keys(obj).forEach((key) => !obj[key] && obj[key] !== 0 && delete obj[key]);
    return obj;
};

export const escapeSingleQueryParam = <T>(obj: T): string | readonly string[] => {
    const XssOptions = {
        whiteList: {},
        stripIgnoreTag: true,
        stripIgnoreTagBody: ['script'],
    };
    return Array.isArray(obj)
        ? obj.map((val) => filterXSS(val.toString(), XssOptions))
        : obj
          ? filterXSS(obj.toString(), XssOptions)
          : undefined;
};

export const escapeQueryParams = <T>(obj: T): T => {
    const res = {};
    Object.keys(obj).forEach((key) => {
        res[key] = escapeSingleQueryParam(obj[key]);

        return res[key];
    });
    return res as T;
};

export const queryParamToBoolean = (param: string | readonly string[] | undefined): boolean => queryParamToString(param) === 'true';

export const booleanToQueryParam = (value?: boolean): string | undefined => (value ? 'true' : undefined);

export const queryParamIsNumber = (param: number | string | readonly number[] | readonly string[] | undefined): boolean => {
    if (Array.isArray(param)) {
        return Boolean(typeof param[0] === 'number');
    }

    return Boolean(typeof param === 'number');
};

export const queryParamToDate = (param: string | readonly string[] | undefined): number | undefined => {
    if (Array.isArray(param)) {
        return safeParseClientDate(param[0])?.getTime();
    }

    if (typeof param === 'string') {
        return safeParseClientDate(param)?.getTime();
    }
};

export const queryParamToArray = (param: readonly string[] | string | undefined): readonly string[] =>
    param ? (Array.isArray(param) ? param : [param]) : [];

export const queryParamToInteger = (param: string | readonly string[] | undefined): number | undefined =>
    param ? (Array.isArray(param) ? ~~param[0] : ~~param) : undefined;

export const pageToQueryParam = (value: number | undefined): string | undefined => (value && value > 1 ? `${value}` : undefined);

export const queryParamToCheckboxMap = (toMap?: readonly string[] | string): CheckboxMap | undefined => {
    if (Array.isArray(toMap)) {
        return toMap.reduce((map: CheckboxMap, item: string): CheckboxMap => {
            map[item] = true;
            return map;
        }, {});
    }

    if (typeof toMap === 'string') {
        return { [toMap]: true };
    }
};

export const checkboxMapToQueryParams = (map?: CheckboxMap): readonly string[] | undefined => {
    const values = map ? Object.keys(map).filter((key) => map[key]) : [];
    return values.length === 0 ? undefined : values;
};

export const queryParamToSortOption = (
    sort?: string | readonly string[],
    sortDirection?: string | readonly string[],
): SortOption | undefined => {
    const value = Array.isArray(sort) ? sort[0] : sort;
    const direction = Array.isArray(sortDirection) ? sortDirection[0] : sortDirection;

    return value && direction ? { value, direction } : undefined;
};

export const sortToQueryParam = (sortOption?: SortOption): string | undefined => sortOption?.value;

export const sortDirectionToQueryParam = (sortOption?: SortOption): string | undefined => sortOption?.direction;

export const openNumberRangeToQueryParam = (range?: OpenNumberRange): string | undefined =>
    range ? `${range[0]}-${range[1] ?? '*'}` : undefined;

export const queryParamsToOpenNumberRange = (toRange?: readonly string[] | string): OpenNumberRange | undefined => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : (toRange ?? '')).split('-');
    return end ? [~~start, ~~end || undefined] : undefined;
};

const parseRoom = (room?: string): RoomWithPeople | undefined => {
    if (!room) {
        return;
    }

    const [adults, children] = room.split('-');

    if (!children) {
        return { adults: Math.floor(Number(adults)), children: [] };
    }

    return { adults: Math.floor(Number(adults)), children: children.split('.').map((age) => Math.floor(Number(age))) };
};

export const queryParamToRooms = (queryParamRooms: string | readonly string[]): RoomWithPeople[] | undefined => {
    const rooms = queryParamToArray(queryParamRooms);

    if (rooms?.length === 0) {
        return undefined;
    }

    return rooms.map(parseRoom).filter(Boolean);
};

export const queryParamToDaysAheadStart = (value: string | readonly string[]): Date | number | undefined => {
    const days = isNumberOrStringNumber(queryParamToString(value)) ? queryParamToInteger(value) : undefined;
    return days || days === 0 ? addDays(days, today()).getTime() : undefined;
};

export const queryParamToDaysAheadEnd = (
    days: string | readonly string[],
    nights: string | readonly string[],
): Date | number | undefined => {
    const start = isNumberOrStringNumber(queryParamToString(days)) ? queryParamToInteger(days) : undefined;
    return start || start === 0 ? addDays(start + (queryParamToInteger(nights) || 1), today()).getTime() : undefined;
};

export const queryParamToDayOfWeekStart = (value: string | readonly string[] | undefined): Date | number => {
    const weekday = stringToDayOfWeek(queryParamToString(value));
    return weekday ? nextDay(weekday)(today()).getTime() : undefined;
};

export const queryParamToDayOfWeekEnd = (
    value: string | readonly string[] | undefined,
    nights: string | readonly string[] | undefined,
): Date | number => {
    const weekday = stringToDayOfWeek(queryParamToString(value));
    return weekday ? addDays(queryParamToInteger(nights) || 1, nextDay(weekday)(today())).getTime() : undefined;
};

export const roomsToQueryParams = (rooms?: readonly RoomWithPeople[]): readonly string[] | undefined =>
    rooms && rooms.length > 0
        ? rooms.map((room) => (room.children && room.children.length > 0 ? `${room.adults}-${room.children.join('.')}` : `${room.adults}`))
        : undefined;

export const roomsToQuery = (rooms: readonly RoomWithPeople[]): readonly string[] =>
    (roomsToQueryParams(rooms) ?? []).map((room) => `rooms=${room}`);

const queryParamToFloat = (param: string | readonly string[] | undefined): number | undefined => {
    if (Array.isArray(param)) {
        return Number.parseFloat(param[0]);
    }

    if (typeof param === 'string') {
        return Number.parseFloat(param);
    }
};

export const queryParamsToMapBounds = (
    lngSw: string | readonly string[] | undefined,
    latSw: string | readonly string[] | undefined,
    lngNe: string | readonly string[] | undefined,
    latNe: string | readonly string[] | undefined,
): MapBounds | undefined => {
    const lngSwValue = isNumberOrStringNumber(queryParamToString(lngSw)) ? queryParamToFloat(String(lngSw)) : undefined;
    const latSwValue = isNumberOrStringNumber(queryParamToString(latSw)) ? queryParamToFloat(String(latSw)) : undefined;
    const lngNeValue = isNumberOrStringNumber(queryParamToString(lngNe)) ? queryParamToFloat(String(lngNe)) : undefined;
    const latNeValue = isNumberOrStringNumber(queryParamToString(latNe)) ? queryParamToFloat(String(latNe)) : undefined;

    if (lngSwValue && latSwValue && lngNeValue && latNeValue) {
        return {
            sw: [lngSwValue, latSwValue],
            ne: [lngNeValue, latNeValue],
        };
    }

    return undefined;
};

export const coordinateToQueryParam = (coordinates?: LngLat, index = 0): string | undefined =>
    coordinates?.[index] ? `${coordinates[index]}` : undefined;

const isNumberOrStringNumber = (str: string): boolean => /^-?[\d.]+(?:e-?\d+)?$/.test(str);
